import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BedDouble, Bath, LandPlot, Home, KeyRound, MapPin, Phone, Mail, MessageCircle, Car, Layers, PlayCircle } from "lucide-react";
import { getListing, imgUrl } from "../lib/api";
import { EnquiryForm } from "../components/EnquiryForm";
import { Reveal } from "../components/Reveal";
import { CONTACT } from "../lib/site";

export default function PropertyDetail() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setListing(null);
    setNotFound(false);
    setActive(0);
    getListing(id).then(setListing).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <main className="flex min-h-[70vh] items-center justify-center pt-[72px]" data-testid="listing-not-found">
        <div className="text-center">
          <p className="font-serif text-4xl text-ivory">This residence is no longer in the collection.</p>
          <Link to="/properties" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-ivory/80 hover:text-ivory">
            <ArrowLeft className="h-4 w-4" /> Back to all residences
          </Link>
        </div>
      </main>
    );
  }

  if (!listing) {
    return (
      <main className="pt-[72px]" data-testid="listing-loading">
        <div className="mx-auto max-w-7xl animate-pulse px-6 py-16 lg:px-8">
          <div className="aspect-[16/8] bg-ivory/10" />
          <div className="mt-8 h-8 w-1/3 bg-ivory/10" />
        </div>
      </main>
    );
  }

  const images = (listing.images || []).map(imgUrl);
  const specs = [
    { icon: Home, label: "Type", value: listing.type },
    { icon: KeyRound, label: "Tenure", value: listing.tenure },
    { icon: BedDouble, label: "Bedrooms", value: listing.beds || "—" },
    { icon: Bath, label: "Bathrooms", value: listing.baths || "—" },
    listing.land_area > 0 && { icon: LandPlot, label: "Land area", value: `${listing.land_area.toLocaleString()} sqft` },
    listing.built_up > 0 && { icon: Home, label: "Built-up", value: `${listing.built_up.toLocaleString()} sqft` },
    listing.storeys > 0 && { icon: Layers, label: "Storeys", value: listing.storeys },
    listing.parking > 0 && { icon: Car, label: "Parking", value: listing.parking },
    { icon: MapPin, label: "District", value: `${listing.district} · ${listing.district_name}` },
    { icon: KeyRound, label: "Status", value: listing.status },
  ].filter(Boolean);

  return (
    <main className="pt-[72px]" data-testid="property-detail-page">
      <section className="mx-auto max-w-7xl px-6 pt-10 lg:px-8">
        <Link to="/properties" data-testid="listing-back-link" className="inline-flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-ivory/60 transition-colors hover:text-ivory">
          <ArrowLeft className="h-4 w-4" /> All residences
        </Link>

        {images.length > 0 && (
          <Reveal className="mt-6">
            <div className="relative aspect-[16/9] overflow-hidden bg-ivory/5 sm:aspect-[16/7]">
              <img
                src={images[active]}
                alt={`${listing.title} — image ${active + 1}`}
                className="h-full w-full object-cover"
                data-testid="listing-gallery-main"
              />
              <div className="absolute left-4 top-4 flex gap-2">
                <span className="border border-ivory/20 bg-charcoal/85 px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ivory">{listing.status}</span>
                {listing.is_sample && (
                  <span className="border border-ivory/40 bg-charcoal/60 px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ivory/85">Sample</span>
                )}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-3">
              {images.map((img, i) => (
                <button
                  key={img + i}
                  type="button"
                  onClick={() => setActive(i)}
                  data-testid={`listing-thumb-${i}`}
                  aria-label={`Show image ${i + 1}`}
                  className={`aspect-[4/3] overflow-hidden border-2 transition-colors duration-300 ${i === active ? "border-ivory" : "border-transparent opacity-70 hover:opacity-100"}`}
                >
                  <img src={img} alt="" loading="lazy" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </Reveal>
        )}
      </section>

      <section className="mx-auto grid max-w-7xl gap-14 px-6 py-14 lg:grid-cols-12 lg:px-8 lg:py-20">
        <div className="lg:col-span-8">
          <Reveal>
            <p className="eyebrow text-ivory/60">{listing.ref} · {listing.district} · {listing.location}</p>
            <h1 className="mt-4 font-serif text-4xl leading-tight text-ivory sm:text-5xl" data-testid="listing-title">{listing.title}</h1>
            <p className="mt-4 font-serif text-3xl font-semibold text-ivory" data-testid="listing-price">{listing.price_display}</p>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mt-10 grid grid-cols-2 gap-px border border-ivory/10 bg-ivory/10 sm:grid-cols-4" data-testid="listing-specs">
              {specs.map((s) => (
                <div key={s.label} className="bg-[#242424] px-5 py-6">
                  <s.icon className="h-4 w-4 text-ivory/50" />
                  <p className="mt-3 text-[0.65rem] uppercase tracking-[0.2em] text-ivory/50">{s.label}</p>
                  <p className="mt-1 text-sm font-medium text-ivory">{s.value}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <h2 className="mt-14 font-serif text-3xl text-ivory">The residence</h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-ivory/70" data-testid="listing-description">{listing.description}</p>

            {listing.features?.length > 0 && (
              <div className="mt-8" data-testid="listing-features">
                <p className="eyebrow text-ivory/60">Features</p>
                <ul className="mt-4 grid max-w-2xl grid-cols-1 gap-2 sm:grid-cols-2">
                  {listing.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-ivory/70">
                      <span className="mt-[0.45rem] h-1.5 w-1.5 shrink-0 rotate-45 bg-ivory/40" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {listing.amenities && (
              <p className="mt-6 max-w-2xl text-sm leading-relaxed text-ivory/60" data-testid="listing-amenities">
                <span className="eyebrow mr-2 text-ivory/50">Nearby</span> {listing.amenities}
              </p>
            )}

            {listing.video_url && (
              <a href={listing.video_url} target="_blank" rel="noopener noreferrer" data-testid="listing-video-link"
                className="mt-8 inline-flex items-center gap-3 border border-ivory/30 px-6 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-ivory transition-colors hover:bg-ivory hover:text-charcoal">
                <PlayCircle className="h-5 w-5" /> Watch the video tour
              </a>
            )}

            {listing.disclaimer && (
              <div className="mt-8 border border-ivory/25 bg-white/5 p-6" data-testid="listing-disclaimer">
                <p className="text-xs leading-relaxed text-ivory/60">{listing.disclaimer}</p>
              </div>
            )}
          </Reveal>
        </div>

        <aside className="lg:col-span-4">
          <Reveal delay={0.1} className="lg:sticky lg:top-28">
            <div className="border border-ivory/10 bg-[#242424] p-8" data-testid="listing-agent-card">
              <div className="flex items-center gap-4">
                <img src="/andy-pro.png" alt="Andy Cheng — Founder, PNG Landed Excellence" className="h-16 w-16 rounded-full border border-ivory/20 object-cover object-top" />
                <div>
                  <p className="font-serif text-xl text-ivory">{CONTACT.agent}</p>
                  <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ivory/50">CEA {CONTACT.ceaReg} · {CONTACT.agency}</p>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-3 gap-2">
                <a href={CONTACT.phoneHref} data-testid="listing-call-button" className="flex flex-col items-center gap-1 border border-ivory/15 py-3 text-[0.6rem] font-semibold uppercase tracking-[0.15em] text-ivory transition-colors hover:border-ivory">
                  <Phone className="h-4 w-4" /> Call
                </a>
                <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="listing-whatsapp-button" className="flex flex-col items-center gap-1 border border-ivory/15 py-3 text-[0.6rem] font-semibold uppercase tracking-[0.15em] text-ivory transition-colors hover:border-ivory">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
                <a href={`mailto:${CONTACT.email}?subject=Enquiry: ${encodeURIComponent(listing.title)}`} data-testid="listing-email-button" className="flex flex-col items-center gap-1 border border-ivory/15 py-3 text-[0.6rem] font-semibold uppercase tracking-[0.15em] text-ivory transition-colors hover:border-ivory">
                  <Mail className="h-4 w-4" /> Email
                </a>
              </div>
            </div>
            <div className="mt-6 border border-ivory/10 bg-[#242424] p-8">
              <p className="eyebrow text-ivory/60">Enquire about this residence</p>
              <div className="mt-5">
                <EnquiryForm kind="property" listingId={listing.id} listingTitle={listing.title} compact />
              </div>
            </div>
          </Reveal>
        </aside>
      </section>
    </main>
  );
}
